import { Suspense, lazy } from 'react';
import { useAuthentication } from 'v2/hooks/useAuthentication';
import { compareVersions } from './selectUnseenNotes';
import { useLastSeenVersion } from './useLastSeenVersion';

const UnseenNotes = lazy(() => import('./UnseenNotes'));

// A Vite define; read at render time so tests can set it on `globalThis`.
const appVersion = () => (typeof __APP_VERSION__ === 'undefined' ? null : __APP_VERSION__);

function WhatsNewForUser({ userId, appVersion }: { userId: string; appVersion: string }) {
  const { lastSeen, markSeen } = useLastSeenVersion(userId, appVersion);

  // Most visits stop here: the user has already seen this bundle's version, so the
  // notes chunk is never fetched.
  const isBehind = lastSeen !== null && (compareVersions(lastSeen, appVersion) ?? 0) < 0;
  if (!isBehind) return null;

  return (
    <Suspense fallback={null}>
      <UnseenNotes lastSeen={lastSeen} appVersion={appVersion} onSeen={markSeen} />
    </Suspense>
  );
}

/** Mounted once, app-wide, in `ClientApp` (`src/app.js`). */
export default function WhatsNew() {
  const status = useAuthentication((state) => state.status);
  const user = useAuthentication((state) => state.user);
  const version = appVersion();

  if (status !== 'authenticated' || !user || user.membership === null || !version) return null;

  // Keyed so switching account re-reads the new user's key from scratch.
  return <WhatsNewForUser key={user.id} userId={user.id} appVersion={version} />;
}
